import React, { useState, useEffect } from 'react';
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { ChevronDown, Loader2, Paperclip, FileText, Image, X, Search, Check } from "lucide-react";
import { cn } from "../ui/utils";
import { toast } from "sonner";
import api from "../../utils/api";
import { 
  Popover,
  PopoverContent,
  PopoverTrigger 
} from "../ui/popover";
import { 
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

export interface FormField {
  id: string;
  type: string;
  label: string;
  description?: string;
  placeholder?: string;
  required?: boolean;
  options?: string[];
  showContainer?: boolean;
  containerColor?: 'gray' | 'blue' | 'green' | 'amber' | 'red';
  showDivider?: boolean;
  selectionMode?: 'single' | 'multi';
  dataSource?: string;
}

interface DynamicFormFieldsProps {
  fields: FormField[];
  values: Record<string, any>;
  onChange: (fieldId: string, value: any) => void;
  isPreview?: boolean;
  isFetching?: boolean;
  showSectionTitle?: boolean;
  sectionTitle?: string;
  sectionSubtitle?: string;
  emptyMessage?: string;
}

export const DynamicFormFields: React.FC<DynamicFormFieldsProps> = ({
  fields,
  values,
  onChange,
  isPreview = false,
  isFetching = false,
  showSectionTitle = true,
  sectionTitle = "Additional Information",
  sectionSubtitle = "Please complete the following specific fields for this request type",
  emptyMessage = "No additional fields required for this request type."
}) => {
  const [dataCache, setDataCache] = useState<Record<string, any[]>>({});
  const [loadingSources, setLoadingSources] = useState<Record<string, boolean>>({});

  const fetchData = async (source: string) => {
    if (dataCache[source] || loadingSources[source]) return;

    setLoadingSources(prev => ({ ...prev, [source]: true }));
    try {
      const response = await api.get(`/api/service-desk/reference-data/${source}`);
      
      if (!response.success) {
        throw new Error(response.message || "Failed to fetch data");
      }

      const data = response.data || [];

      // Map to standard label/value
      const mappedData = data.map((item: any) => {
        let label = item.name || item.roleName || item.procedureName || item.trainingPlanName || item.projectName || item.tradingName || item.legalName || item.title || "Unknown";
        let subtitle = item.tag || item.sku || item.projectNumber || item.category || item.type || (item.department?.name) || "";
        
        // Special case for qualifications/training plans which are often linked to people
        if (source === 'qualifications' && item.person) label = `${item.name} (${item.person.name})`;
        if (source === 'trainingPlans' && item.trainee) subtitle = `Trainee: ${item.trainee.name}`;

        return {
          id: String(item.id),
          label: label,
          subtitle: subtitle
        };
      });

      setDataCache(prev => ({ ...prev, [source]: mappedData }));
    } catch (error: any) {
      console.error(`Error fetching ${source}:`, error);
      toast.error(`Failed to load ${source} data: ${error.message || 'Unknown error'}`);
    } finally {
      setLoadingSources(prev => ({ ...prev, [source]: false }));
    }
  };

  const getFileIcon = (filename: string) => {
    if (!filename) return <Paperclip className="w-4 h-4 text-gray-400 shrink-0" />;
    
    const ext = filename.split('.').pop()?.toLowerCase();
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'heic'].includes(ext || '')) {
        return <Image className="w-4 h-4 text-blue-500 shrink-0" />;
    }
    
    if (ext === 'pdf') {
        return <FileText className="w-4 h-4 text-red-500 shrink-0" />;
    }
    
    if (['doc', 'docx', 'txt', 'rtf'].includes(ext || '')) {
        return <FileText className="w-4 h-4 text-blue-600 shrink-0" />;
    }
    
    if (['xls', 'xlsx', 'csv', 'ods'].includes(ext || '')) {
        return <FileText className="w-4 h-4 text-green-600 shrink-0" />;
    }

    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext || '')) {
        return <FileText className="w-4 h-4 text-orange-600 shrink-0" />;
    }
    
    return <FileText className="w-4 h-4 text-gray-400 shrink-0" />;
  };

  if (isFetching) {
    return (
      <div className="flex flex-col items-center justify-center py-12 border-t border-gray-100 space-y-3">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Loading specialized fields...</p>
      </div>
    );
  }

  if (fields.length === 0) {
    if (emptyMessage) {
        return (
            <div className="py-8 border-t border-gray-100 text-center">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">{emptyMessage}</p>
            </div>
        );
    }
    return null;
  }

  return (
    <div className="space-y-6 pt-6 border-t border-gray-100 animate-in fade-in slide-in-from-bottom-2 duration-300">
      {showSectionTitle && (
        <div>
          <h3 className="text-lg font-bold text-gray-900">{sectionTitle}</h3>
          <p className="text-xs text-muted-foreground font-medium mt-1 uppercase tracking-tight">{sectionSubtitle}</p>
        </div>
      )}

      <div className="space-y-6">
        {fields.map((field) => (
          <div key={field.id} className="space-y-2">
            {!['heading', 'static_text'].includes(field.type) && (
              <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}
            {!['heading', 'static_text'].includes(field.type) && field.description && <p className="text-xs text-gray-400 font-medium mb-2">{field.description}</p>}

            {field.type === 'heading' && (
              <div className={cn(
                "pt-6 pb-2 mb-2 mt-8 first:mt-0",
                field.showDivider !== false ? "border-b border-gray-100" : ""
              )}>
                <h3 className="text-lg font-bold text-gray-900">{field.label}</h3>
                {field.description && <p className="text-sm text-gray-500 font-medium mt-1">{field.description}</p>}
              </div>
            )}

            {field.type === 'static_text' && (
              <div className={cn(
                "rounded-2xl transition-all my-2",
                field.showContainer ? cn(
                  "py-4 px-5 border",
                  field.containerColor === 'blue' ? "bg-blue-50/40 border-blue-100/50 text-blue-900" :
                  field.containerColor === 'green' ? "bg-green-50/40 border-green-100/50 text-green-900" :
                  field.containerColor === 'amber' ? "bg-amber-50/40 border-amber-100/50 text-amber-900" :
                  field.containerColor === 'red' ? "bg-red-50/40 border-red-100/50 text-red-900" :
                  "bg-gray-50/40 border-gray-100/50 text-gray-900"
                ) : "p-0"
              )}>
                {field.label && (
                  <h4 className={cn(
                    "text-sm font-bold mb-1.5 uppercase tracking-tight",
                    field.showContainer ? "" : "text-gray-900"
                  )}>
                    {field.label}
                  </h4>
                )}
                <div className={cn(
                  "text-sm whitespace-pre-wrap leading-relaxed font-medium",
                  field.showContainer ? "opacity-80" : "text-gray-600"
                )}>
                  {field.description}
                </div>
              </div>
            )}

            {field.type === 'short_text' && (
              <Input
                className="h-11 bg-gray-50 border-gray-200 rounded-xl focus:ring-primary/20 font-medium"
                placeholder={field.placeholder}
                value={values[field.id] || ''}
                onChange={(e) => onChange(field.id, e.target.value)}
                required={field.required}
                disabled={isPreview}
              />
            )}

            {field.type === 'long_text' && (
              <Textarea
                className="min-h-[120px] bg-gray-50 border-gray-200 rounded-xl resize-y focus:ring-primary/20 font-medium"
                placeholder={field.placeholder}
                value={values[field.id] || ''}
                onChange={(e) => onChange(field.id, e.target.value)}
                required={field.required}
                disabled={isPreview}
              />
            )}

            {field.type === 'idarah_data' && (
              <IdarahDataSelector 
                field={field}
                value={values[field.id]}
                onChange={(val) => onChange(field.id, val)}
                isPreview={isPreview}
                onOpen={() => fetchData(field.dataSource || "")}
                options={dataCache[field.dataSource || ""] || []}
                isLoading={loadingSources[field.dataSource || ""]}
              />
            )}

            {field.type === 'dropdown' && (
              <div className="relative">
                <select
                  className="w-full h-11 px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm font-medium appearance-none cursor-pointer disabled:opacity-50"
                  value={values[field.id] || ''}
                  onChange={(e) => onChange(field.id, e.target.value)}
                  required={field.required}
                  disabled={isPreview}
                >
                  <option value="" disabled>{field.placeholder || "Select an option"}</option>
                  {field.options?.map((opt, i) => (
                    <option key={i} value={opt}>{opt}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            )}

            {['checkbox', 'multi_select'].includes(field.type) && (
              <div className="space-y-2 pt-1">
                {field.options?.map((opt, i) => (
                  <label key={i} className="flex items-center gap-3 cursor-pointer group w-fit">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary/20 disabled:opacity-50"
                      checked={(values[field.id] || []).includes(opt)}
                      onChange={(e) => {
                        const current = values[field.id] || [];
                        const newVal = e.target.checked
                          ? [...current, opt]
                          : current.filter((v: string) => v !== opt);
                        onChange(field.id, newVal);
                      }}
                      disabled={isPreview}
                    />
                    <span className="text-sm text-gray-600 font-medium group-hover:text-gray-900 transition-colors">{opt}</span>
                  </label>
                ))}
              </div>
            )}

            {field.type === 'radio' && (
              <div className="space-y-2 pt-1">
                {field.options?.map((opt, i) => (
                  <label key={i} className="flex items-center gap-3 cursor-pointer group w-fit">
                    <input
                      type="radio"
                      name={`radio-${field.id}`}
                      className="w-4 h-4 border-gray-300 text-primary focus:ring-primary/20 disabled:opacity-50"
                      checked={values[field.id] === opt}
                      onChange={() => onChange(field.id, opt)}
                      disabled={isPreview}
                    />
                    <span className="text-sm text-gray-600 font-medium group-hover:text-gray-900 transition-colors">{opt}</span>
                  </label>
                ))}
              </div>
            )}

            {['email', 'number', 'date', 'time', 'url', 'phone'].includes(field.type) && (
              <Input
                type={field.type === 'phone' ? 'tel' : (field.type === 'number' ? 'number' : field.type)}
                className="h-11 bg-gray-50 border-gray-200 rounded-xl focus:ring-primary/20 font-medium"
                placeholder={field.placeholder}
                value={values[field.id] || ''}
                onChange={(e) => onChange(field.id, e.target.value)}
                required={field.required}
                disabled={isPreview}
              />
            )}

            {field.type === 'file' && (
              <div className="space-y-3">
                <div className="relative">
                  <input
                    type="file"
                    className="hidden"
                    id={`file-upload-${field.id}`}
                    onChange={(e) => {
                      if (e.target.files) {
                        const newFiles = Array.from(e.target.files);
                        const current = values[field.id] || [];
                        
                        // --- Constraints Validation ---
                        const MAX_FILES = 10;
                        const MAX_SIZE = 10 * 1024 * 1024; // 10MB
                        
                        if (current.length + newFiles.length > MAX_FILES) {
                            toast.error(`Maximum ${MAX_FILES} files allowed per field.`);
                            return;
                        }

                        const oversizedFiles = newFiles.filter(f => f.size > MAX_SIZE);
                        if (oversizedFiles.length > 0) {
                            toast.error(`${oversizedFiles.length} file(s) exceed the 10MB limit.`);
                            return;
                        }

                        onChange(field.id, [...current, ...newFiles]);
                      }
                    }}
                    multiple
                    disabled={isPreview}
                  />
                  <label
                    htmlFor={`file-upload-${field.id}`}
                    className={cn(
                      "flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-200 rounded-xl font-bold transition-all cursor-pointer hover:bg-gray-50",
                      isPreview ? "opacity-50 cursor-not-allowed" : "hover:border-primary/30"
                    )}
                  >
                    <Paperclip className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{field.placeholder || "Upload files"}</span>
                  </label>
                </div>

                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest text-center">
                  Max 10 files, 10MB each (PDF, JPG, PNG)
                </p>

                {values[field.id] && values[field.id].length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                    {values[field.id].map((file: File, idx: number) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-xl border border-gray-100 bg-white shadow-sm group hover:border-primary/20 transition-all">
                        <div className="flex items-center min-w-0">
                          <div className="p-2 rounded-lg bg-gray-50 mr-3 border border-gray-100">
                             {getFileIcon(file.name)}
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm font-bold text-gray-700 truncate" title={file.name}>{file.name}</span>
                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const current = values[field.id] || [];
                            const newVal = current.filter((_: any, i: number) => i !== idx);
                            onChange(field.id, newVal);
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                          title="Remove file"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

interface IdarahDataSelectorProps {
  field: FormField;
  value: any;
  onChange: (val: any) => void;
  isPreview: boolean;
  onOpen: () => void;
  options: any[];
  isLoading: boolean;
}

const IdarahDataSelector: React.FC<IdarahDataSelectorProps> = ({
  field,
  value,
  onChange,
  isPreview,
  onOpen,
  options,
  isLoading
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const isMulti = field.selectionMode === 'multi';
  const selectedIds = isMulti ? (Array.isArray(value) ? value : []) : (value ? [value] : []);

  const handleToggle = (id: string) => {
    if (isMulti) {
      const current = Array.isArray(value) ? value : [];
      const newVal = current.includes(id)
        ? current.filter(v => v !== id)
        : [...current, id];
      onChange(newVal);
    } else {
      onChange(id);
      setOpen(false);
    }
  };

  const selectedLabels = options
    .filter(opt => selectedIds.includes(opt.id))
    .map(opt => opt.label);

  const filteredOptions = options.filter(opt => 
    opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (opt.subtitle && opt.subtitle.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getSourceLabel = (source?: string) => {
    switch (source) {
      case 'businessSystems': return "Business Systems";
      case 'assets': return "Assets";
      case 'inventoryItems': return "Inventory Items";
      case 'qualifications': return "Qualifications";
      case 'trainingPlans': return "Training Plans";
      case 'jobRoles': return "Job Roles";
      case 'procedures': return "Procedures";
      case 'projects': return "Projects";
      case 'branches': return "Branches";
      case 'customers': return "Customers";
      case 'suppliers': return "Suppliers";
      default: return source || "Data";
    }
  };

  const sourceLabel = getSourceLabel(field.dataSource);

  return (
    <div className="space-y-2">
      <Popover 
        open={open} 
        onOpenChange={(isOpen) => {
          if (!isPreview) {
            setOpen(isOpen);
            if (isOpen) onOpen();
          }
        }}
      >
        <PopoverTrigger asChild>
          <div 
            className={cn(
              "w-full h-11 px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-between text-sm font-medium transition-all",
              isPreview ? "opacity-70 cursor-not-allowed" : "cursor-pointer hover:border-primary/30",
              open ? "ring-2 ring-primary/20 border-primary/30 bg-white" : ""
            )}
          >
            <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
              {selectedLabels.length > 0 ? (
                <div className="flex flex-wrap gap-1.5 max-w-full overflow-hidden">
                  {selectedLabels.slice(0, 2).map((label, idx) => (
                    <Badge 
                      key={idx} 
                      variant="secondary" 
                      className="bg-gray-100 text-black border-gray-200 font-bold text-[11px] px-2 py-0.5 whitespace-nowrap"
                    >
                      {label}
                    </Badge>
                  ))}
                  {selectedLabels.length > 2 && (
                    <Badge variant="secondary" className="bg-gray-100 text-gray-600 font-bold text-[11px] px-2 py-0.5">
                      +{selectedLabels.length - 2} more
                    </Badge>
                  )}
                </div>
              ) : (
                <span className="text-gray-400 font-medium">
                  {field.placeholder || (isMulti 
                    ? `Select ${sourceLabel} (Multi-select)` 
                    : `Select ${sourceLabel}...`)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-2">
              <ChevronDown className={cn("h-4 w-4 text-gray-400 transition-transform duration-200", open ? "rotate-180" : "")} />
            </div>
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-white shadow-2xl border-slate-200 rounded-2xl overflow-hidden" align="start">
          <div className="flex flex-col max-h-[480px]">
            {/* Search Header - Match Qualification Register */}
            <div className="p-3 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <Input
                  placeholder={`Search ${sourceLabel}...`}
                  className="pl-8 h-9 text-sm bg-slate-50 border-none focus-visible:ring-slate-200 focus:ring-0"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                />
              </div>
            </div>

            {/* List Content */}
            <div className="flex-1 overflow-y-auto p-2 space-y-1 custom-scrollbar min-h-[100px] max-h-[350px]">
              {isLoading && options.length === 0 ? (
                <div className="p-8 text-center">
                  <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto mb-2" />
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Fetching Data...</p>
                </div>
              ) : filteredOptions.length === 0 ? (
                <div className="p-8 text-center">
                  <p className="text-xs text-slate-400 font-medium italic">No results found</p>
                </div>
              ) : (
                <div className="space-y-0.5">
                  {filteredOptions.map((opt) => {
                    const isSelected = selectedIds.includes(opt.id);
                    return (
                      <div
                        key={opt.id}
                        onClick={() => handleToggle(opt.id)}
                        className={cn(
                          "flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-all group",
                          isSelected 
                            ? "bg-slate-900 text-white shadow-sm" 
                            : "text-slate-700 hover:bg-slate-50"
                        )}
                      >
                        <div className="flex flex-col min-w-0 py-0.5">
                          <span className={cn("text-sm font-bold truncate", isSelected ? "text-white" : "text-slate-900")}>
                            {opt.label}
                          </span>
                          {opt.subtitle && (
                            <span className={cn(
                              "text-[10px] font-bold uppercase tracking-tight truncate",
                              isSelected ? "text-slate-400" : "text-slate-500"
                            )}>
                              {opt.subtitle}
                            </span>
                          )}
                        </div>
                        {isSelected && <Check className="h-4 w-4 shrink-0 text-white" />}
                        {!isSelected && isMulti && (
                          <div className="w-4 h-4 rounded border border-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer - For Multi-select */}
            {isMulti && selectedIds.length > 0 && (
              <div className="p-2 border-t border-slate-100 bg-slate-50/50 flex justify-between items-center">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider pl-2">
                  {selectedIds.length} Selected
                </span>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 text-[10px] font-bold uppercase tracking-tight text-red-500 hover:text-red-600 hover:bg-red-50"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onChange([]);
                  }}
                >
                  Clear All
                </Button>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
      {isPreview && (
         <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest ml-1">
           Dynamic reference to Idarah {field.dataSource} table
         </p>
      )}
    </div>
  );
};
